<template>
	<div class="public-share-calendar">
		<engage-loader v-if="loading" overlay />

		<main
			v-if="!loading && !hasCalendars"
			class="public-share-calendar__empty"
		>
			<p class="public-share-calendar__empty-message">
				No Calendars are Shared
			</p>
		</main>

		<template v-else>
			<header class="public-share-calendar__header">
				<div class="public-share-calendar__brand">
					<div class="public-share-calendar__brand-logo">
						<img
							class="public-share-calendar__logo"
							src="@/assets/img/logo.png"
							alt="Clarus Care logo"
						/>
					</div>
					<div
						class="public-share-calendar__brand-divider"
						aria-hidden="true"
					></div>
					<div class="public-share-calendar__brand-text">
						<h1 class="public-share-calendar__title">
							{{ partnerName }}
						</h1>
						<p
							v-if="afterHoursPhoneNumber"
							class="public-share-calendar__subtitle"
						>
							Office Number:
							{{ afterHoursPhoneNumber | newFormatPhoneNumber }}
						</p>
					</div>
				</div>
				<div ref="exportDropdown" class="public-share-calendar__export">
					<button
						type="button"
						class="public-share-calendar__export-btn"
						aria-haspopup="true"
						aria-label="Export calendar"
						:aria-expanded="showExportMenu.toString()"
						@click.prevent.stop="onExportMenuToggle"
					>
						<download-icon
							class="public-share-calendar__export-icon"
						/>
						<span class="public-share-calendar__export-label"
							>Export</span
						>
						<font-awesome-icon
							class="public-share-calendar__export-chevron"
							fixed-width
							:icon="['fal', 'chevron-down']"
							aria-hidden="true"
						/>
					</button>
					<div
						v-show="showExportMenu"
						class="public-share-calendar__export-menu"
						role="menu"
						aria-label="Export options"
					>
						<button
							type="button"
							class="public-share-calendar__export-menu-item"
							role="menuitem"
							@click.prevent="onPrint"
						>
							<print-icon
								class="public-share-calendar__export-menu-icon"
							/>
							<span>Print</span>
						</button>
						<button
							type="button"
							class="public-share-calendar__export-menu-item"
							role="menuitem"
							@click.prevent="onSaveAsPdf"
						>
							<save-as-pdf-icon
								class="public-share-calendar__export-menu-icon"
							/>
							<span>Save as PDF</span>
						</button>
					</div>
				</div>
			</header>

			<div class="public-share-calendar__print-header">
				<div class="public-share-calendar__print-header-left">
					<p class="public-share-calendar__print-partner">
						{{ partnerName }}
					</p>
					<p
						v-if="afterHoursPhoneNumber"
						class="public-share-calendar__print-phone"
					>
						Office Number:
						{{ afterHoursPhoneNumber | newFormatPhoneNumber }}
					</p>
				</div>
				<div class="public-share-calendar__print-header-center">
					<h2 class="public-share-calendar__print-calendar-name">
						{{ activeCalendarName }}
					</h2>
					<p class="public-share-calendar__print-date-range">
						{{ dateRangeLabel }}
					</p>
				</div>
			</div>

			<div class="public-share-calendar__body">
				<aside class="public-share-calendar__sidebar">
					<h2 class="public-share-calendar__sidebar-title">
						Calendars
					</h2>
					<div class="public-share-calendar__sidebar-nav-clip">
						<nav
							class="public-share-calendar__sidebar-nav"
							aria-label="Calendars"
						>
							<router-link
								v-for="calendar in calendars"
								:key="calendar.id"
								:to="getCalendarRoute(calendar.id)"
								class="public-share-calendar__sidebar-item"
								:class="{
									'public-share-calendar__sidebar-item--active': isActiveCalendar(
										calendar.id
									),
								}"
							>
								<font-awesome-icon
									class="public-share-calendar__sidebar-icon"
									fixed-width
									:icon="['fal', 'calendar']"
									aria-hidden="true"
								/>
								<span>{{ calendar.name }}</span>
							</router-link>
						</nav>
					</div>
				</aside>

				<main class="public-share-calendar__main">
					<div class="public-share-calendar__month-bar">
						<div class="public-share-calendar__month-pills">
							<button
								v-for="pill in monthPills"
								:key="pill.key"
								type="button"
								class="public-share-calendar__month-pill"
								:class="{
									'public-share-calendar__month-pill--active':
										pill.isSelected,
									'public-share-calendar__month-pill--current':
										pill.isCurrent,
								}"
								@click.prevent="onMonthPillClick(pill.date)"
							>
								{{ pill.label }}
								<span v-if="pill.isCurrent"> (Current)</span>
							</button>
						</div>
						<label class="public-share-calendar__expand-toggle">
							<span>Expand detail</span>
							<input
								v-model="expandDetail"
								type="checkbox"
								class="public-share-calendar__expand-input"
								@change="onExpandDetailChange"
							/>
							<span
								class="public-share-calendar__expand-switch"
								aria-hidden="true"
							></span>
						</label>
					</div>

					<div
						v-if="activeCalendarId && calendarOptions"
						class="public-share-calendar__grid-scroll-clip"
					>
						<div class="public-share-calendar__grid-scroll">
							<div class="public-share-calendar__grid-wrap">
								<engage-loader
									v-if="isCalendarBusy"
									overlay
								/>
								<full-calendar
									:key="calendarLayoutKey"
									ref="calendar"
									class="public-share-calendar__grid"
									:options="calendarOptions"
								>
									<template #eventContent="{ event }">
										<public-share-event
											:event="event"
											:show-primary-title="expandDetail"
											:show-secondary-title="true"
										/>
									</template>
								</full-calendar>
							</div>
						</div>
					</div>
				</main>
			</div>
		</template>
	</div>
</template>

<script>
import moment from 'moment'
import { sortBy } from 'lodash'
import FullCalendar from '@fullcalendar/vue'
import { mapActions, mapGetters } from 'vuex'
import EngageLoader from '@/components/EngageLoader.vue'
import DownloadIcon from '@/components/Icons/DownloadIcon.vue'
import PrintIcon from '@/components/Icons/PrintIcon.vue'
import SaveAsPdfIcon from '@/components/Icons/SaveAsPdfIcon.vue'
import PublicShareEvent from '@/components/FullCalendar/PublicShareEvent.vue'
import adaptivePlugin from '@fullcalendar/adaptive'
import FullCalendarEvent from 'App/Calendar/FullCalendarEvent'
import FullCalendarConfig from 'App/Calendar/FullCalendarConfig'

const MONTH_PILL_OFFSET = 3
const MONTH_PILL_COUNT = 7
const COLLAPSED_EVENT_ROWS = 4
const ESCAPE_KEY_CODE = 27
const CALENDAR_POLL_INTERVAL_MS = 60000
const PRINT_PAGE_MARGIN_IN = 0.15
const PRINT_PORTRAIT_WIDTH_IN = 8.5
const PRINT_CONTENT_WIDTH_PX =
	(PRINT_PORTRAIT_WIDTH_IN - PRINT_PAGE_MARGIN_IN * 2) * 96

/**
 * Public shared calendar page.
 */
export default {
	components: {
		DownloadIcon,
		EngageLoader,
		FullCalendar,
		PrintIcon,
		PublicShareEvent,
		SaveAsPdfIcon,
	},

	computed: {
		activeCalendarId() {
			const calendarId = parseInt(this.$route.params.id, 10)

			return Number.isNaN(calendarId) ? null : calendarId
		},

		afterHoursPhoneNumber() {
			return this.officeNumber || ''
		},

		activeCalendarName() {
			const calendar = this.calendars.find(
				calendar => calendar.id === this.activeCalendarId
			)

			return calendar?.name || ''
		},

		hasCalendars() {
			return this.calendars.length > 0
		},

		calendars() {
			return sortBy(this.bootstrapCalendars, 'name')
		},

		dateRangeLabel() {
			const monthStart = this.viewMonth.clone().startOf('month')
			const monthEnd = this.viewMonth.clone().endOf('month')

			return `${monthStart.format('MMMM D')} – ${monthEnd.format(
				'D, YYYY'
			)}`
		},

		earliestViewMonth() {
			return moment()
				.startOf('month')
				.subtract(MONTH_PILL_OFFSET, 'months')
		},

		latestViewMonth() {
			return moment()
				.startOf('month')
				.add(MONTH_PILL_OFFSET, 'months')
		},

		monthPills() {
			const anchorMonth = moment().startOf('month')

			return Array.from({ length: MONTH_PILL_COUNT }, (_, index) => {
				const date = anchorMonth
					.clone()
					.subtract(MONTH_PILL_OFFSET, 'months')
					.add(index, 'months')

				return {
					date,
					isCurrent: date.isSame(moment(), 'month'),
					isSelected: date.isSame(this.viewMonth, 'month'),
					key: date.format('YYYY-MM'),
					label: date.format('MMM YY'),
				}
			})
		},

		partnerName() {
			return this.bootstrapPartnerName || 'Scheduling'
		},

		...mapGetters({
			findEventsByCalendarAndDate:
				'calendars/findEventsByCalendarAndDate',
		}),
		...mapGetters('publicCalendar', {
			bootstrapCalendars: 'calendars',
			bootstrapPartnerName: 'partnerName',
			officeNumber: 'officeNumber',
		}),
	},

	methods: {
		addExportMenuListeners() {
			document.addEventListener('click', this.onExportOutsideClick)
			document.addEventListener('keydown', this.onExportEscapeKeyDown)
		},

		closeExportMenu() {
			if (!this.showExportMenu) {
				return
			}

			this.showExportMenu = false
			this.removeExportMenuListeners()
		},

		calendarApi() {
			return this.$refs.calendar?.getApi()
		},

		buildCalendarOptions() {
			return {
				...this.fullCalendarConfig,
				dayMaxEventRows: this.expandDetail
					? false
					: COLLAPSED_EVENT_ROWS,
				eventDisplay: 'block',
				events: this.getCalendarEvents(),
				expandRows: false,
				// FullCalendar's month view defaults to 6 week rows. Only keep
				// weeks that contain a day of the viewed month.
				fixedWeekCount: false,
				height: 'auto',
				initialDate: this.viewMonth.toDate(),
				views: {
					...this.fullCalendarConfig.views,
				},
			}
		},

		getCalendarEvents() {
			if (!this.activeCalendarId) {
				return []
			}

			const { monthEnd, monthStart } = this.getMonthDateRange()

			const calendarEvents =
				this.findEventsByCalendarAndDate(
					this.activeCalendarId,
					monthStart,
					monthEnd
				) || []

			return calendarEvents
				.map(event => {
					const mapped = new FullCalendarEvent(event).toObject()

					mapped.display = 'block'

					return this.clampEventToStartDay(mapped)
				})
				.filter(event =>
					this.isWithinViewMonth(event, monthStart, monthEnd)
				)
		},

		// The month grid also renders leading and trailing days belonging to
		// the neighbouring months. Keep blocks off those cells so only the
		// viewed month's own days carry events.
		isWithinViewMonth(event, monthStart, monthEnd) {
			const start = moment(event.start).local()

			return start.isBetween(monthStart, monthEnd, 'day', '[]')
		},

		// FullCalendar reserves a vertical track on every day a multi-day
		// event covers, which leaves a blank gap at the top of later cells.
		// Keep the original end for the public event label, but give the grid
		// a same-day exclusive end so each block sits at the top of its cell.
		clampEventToStartDay(event) {
			const start = moment(event.start).local()
			const end = moment(event.end).local()

			if (!start.isValid() || !end.isValid() || start.isSame(end, 'day')) {
				return event
			}

			return {
				...event,
				duration: undefined,
				end: start
					.clone()
					.add(1, 'day')
					.startOf('day')
					.toISOString(),
				originalEnd: event.end,
			}
		},

		syncCalendarEvents() {
			const calendarApi = this.calendarApi()

			if (!calendarApi || !this.activeCalendarId) {
				return
			}

			calendarApi.setOption('events', this.getCalendarEvents())
			this.remeasureCalendarColumns()
		},

		// Painting the events can make the page tall enough to add a window
		// scrollbar, which narrows the grid after FullCalendar has measured its
		// column widths and leaves the last column clipped. Re-measure once the
		// new rows have been laid out.
		async remeasureCalendarColumns() {
			await this.$nextTick()
			await this.$nextTick()

			this.calendarApi()?.updateSize()
			this.fitMorePopoverToDateCell()
		},

		// FullCalendar's "+ more" popover is wider than a day cell and opens
		// downward, so last-week / last-column popovers are clipped. Size it to
		// the cell so the event list scrolls inside the frame.
		onMoreLinkClick({ jsEvent }) {
			const dayCell = jsEvent?.target?.closest('.fc-daygrid-day')

			this.scheduleMorePopoverFit(dayCell)

			return 'popover'
		},

		onCalendarWindowResize() {
			this.syncGridScrollbarTrack()
			this.fitMorePopoverToDateCell()
		},

		syncGridScrollbarTrack() {
			const root = this.$el

			if (!root || !root.classList.contains('public-share-calendar')) {
				return
			}

			const probe = document.createElement('div')
			probe.className = 'public-share-calendar__scrollbar-probe'
			root.appendChild(probe)
			const track = Math.max(0, probe.offsetWidth - probe.clientWidth)
			root.removeChild(probe)
			root.style.setProperty(
				'--public-share-scrollbar-track',
				`${track}px`
			)
		},

		scheduleMorePopoverFit(dayCell) {
			let attempts = 0

			const tryFit = () => {
				attempts += 1

				if (
					this.fitMorePopoverToDateCell(dayCell) ||
					attempts >= 8
				) {
					return
				}

				requestAnimationFrame(tryFit)
			}

			requestAnimationFrame(tryFit)
		},

		fitMorePopoverToDateCell(dayCell) {
			const rootEl = this.$el
			const popover = rootEl?.querySelector('.fc-more-popover')

			if (!popover) {
				return false
			}

			const date = popover.getAttribute('data-date')
			const cell =
				dayCell ||
				(date
					? rootEl.querySelector(
							`.fc-daygrid-day[data-date="${date}"]`
					  )
					: null)
			const origin = popover.offsetParent

			if (!cell || !origin) {
				return false
			}

			const cellRect = cell.getBoundingClientRect()
			const originRect = origin.getBoundingClientRect()
			const cellWidth = `${cellRect.width}px`
			const cellHeight = `${cellRect.height}px`
			const header = popover.querySelector('.fc-popover-header')
			const body = popover.querySelector('.fc-popover-body')

			popover.style.boxSizing = 'border-box'
			popover.style.width = cellWidth
			popover.style.minWidth = cellWidth
			popover.style.maxWidth = cellWidth
			popover.style.height = cellHeight
			popover.style.maxHeight = cellHeight
			popover.style.left = `${cellRect.left - originRect.left}px`
			popover.style.top = `${cellRect.top - originRect.top}px`

			if (body) {
				const headerHeight = header ? header.offsetHeight : 0

				body.style.maxHeight = `${Math.max(
					cellRect.height - headerHeight,
					0
				)}px`
				body.style.overflowY = 'scroll'
			}

			return true
		},

		async syncExpandDetailRows() {
			// Remount so other months get a first-paint layout. `render()` on a
			// calendar that already called gotoDate reuses leftover row height.
			const dayMaxEventRows = this.expandDetail
				? false
				: COLLAPSED_EVENT_ROWS

			this.fullCalendarConfig.setDayMaxEventRows(dayMaxEventRows)
			this.calendarOptions = this.buildCalendarOptions()
			this.calendarLayoutKey += 1

			await this.$nextTick()
			this.syncCalendarEvents()
		},

		async ensureActiveCalendar() {
			const shortId = this.$route.params.shortId

			if (!this.calendars.length) {
				if (this.$route.name !== 'public.calendar' && shortId) {
					await this.$router.replace({
						name: 'public.calendar',
						params: { shortId },
					})
				}

				return
			}

			const isActiveCalendarAvailable = this.calendars.some(
				calendar => calendar.id === this.activeCalendarId
			)

			if (isActiveCalendarAvailable) {
				return
			}

			await this.$router.replace(
				this.getCalendarRoute(this.calendars[0].id)
			)
		},

		async loadEventsForActiveCalendar() {
			if (!this.activeCalendarId) {
				return
			}

			const { endsAt, startsAt } = this.getEventQueryDates()

			await this.fetchPublicCalendarEvents({
				calendarId: this.activeCalendarId,
				endsAt,
				startsAt,
			})
			this.syncCalendarEvents()
		},

		async loadCalendarData(route = this.$route) {
			if (
				route.name !== 'public.calendar' &&
				route.name !== 'public.calendar.show'
			) {
				return
			}

			const shortId = route.params.shortId

			if (!shortId) {
				return
			}

			this.loading = true

			try {
				if (!this.hasCalendars) {
					return
				}

				await this.ensureActiveCalendar()

				if (this.activeCalendarId) {
					await this.loadEventsForActiveCalendar()
				}
			} catch (error) {
				this.$alert.error('default.error')
			} finally {
				this.loading = false
				this.startCalendarPolling()
			}
		},

		async getEvents() {
			if (!this.activeCalendarId) {
				return
			}

			this.isCalendarBusy = true

			try {
				await this.loadEventsForActiveCalendar()
			} catch (error) {
				this.$alert.error('default.error')
			} finally {
				this.isCalendarBusy = false
			}
		},

		async refreshBootstrap() {
			const shortId = this.$route.params.shortId

			if (!shortId) {
				return
			}

			try {
				await this.refreshPublicBootstrap(shortId)
				await this.ensureActiveCalendar()
			} catch (error) {
				// Silent refresh — avoid alert spam during polling.
			}
		},

		async refreshEvents() {
			if (!this.activeCalendarId) {
				return
			}

			try {
				await this.loadEventsForActiveCalendar()
			} catch (error) {
				// Silent refresh — avoid alert spam during polling.
			}
		},

		async pollCalendarData() {
			const routeName = this.$route.name
			const isPublicCalendarRoute =
				routeName === 'public.calendar' ||
				routeName === 'public.calendar.show'

			if (document.hidden || !isPublicCalendarRoute || this.loading) {
				return
			}

			await this.refreshBootstrap()

			// Calendars may have reappeared on the empty page and redirected
			// to a show route inside ensureActiveCalendar.
			if (this.$route.name !== 'public.calendar.show') {
				return
			}

			await this.refreshEvents()
		},

		startCalendarPolling() {
			this.stopCalendarPolling()

			if (document.hidden) {
				return
			}

			this.pollIntervalId = window.setInterval(() => {
				this.pollCalendarData()
			}, CALENDAR_POLL_INTERVAL_MS)
		},

		stopCalendarPolling() {
			if (!this.pollIntervalId) {
				return
			}

			clearInterval(this.pollIntervalId)
			this.pollIntervalId = null
		},

		async onVisibilityChange() {
			if (document.hidden) {
				this.stopCalendarPolling()
				return
			}

			await this.pollCalendarData()
			this.startCalendarPolling()
		},

		getEventQueryDates() {
			const { monthEnd, monthStart } = this.getMonthDateRange()

			return {
				endsAt: monthEnd.format('YYYY-MM-DD'),
				startsAt: monthStart.format('YYYY-MM-DD'),
			}
		},

		getMonthDateRange(viewMonth = this.viewMonth) {
			const monthStart = viewMonth.clone().startOf('month')
			const monthEnd = viewMonth.clone().endOf('month')

			return {
				monthEnd,
				monthStart,
			}
		},

		syncMonthDateRange(viewMonth = this.viewMonth) {
			const { monthEnd, monthStart } = this.getMonthDateRange(viewMonth)

			this.viewMonth = monthStart.clone()
			this.startDate = monthStart
			this.endDate = monthEnd
		},

		getCalendarRoute(calendarId) {
			return {
				name: 'public.calendar.show',
				params: {
					id: calendarId,
					shortId: this.$route.params.shortId,
				},
			}
		},

		isActiveCalendar(calendarId) {
			return this.activeCalendarId === calendarId
		},

		onDatesSet({ view }) {
			const nextViewMonth = moment(view.currentStart).startOf('month')

			if (nextViewMonth.isSame(this.viewMonth, 'month')) {
				return
			}

			this.syncMonthDateRange(nextViewMonth)

			if (this.activeCalendarId && !this.loading) {
				this.getEvents()
			}
		},

		async onExpandDetailChange() {
			await this.syncExpandDetailRows()
		},

		onExportEscapeKeyDown(event) {
			if (this.showExportMenu && event.keyCode === ESCAPE_KEY_CODE) {
				this.closeExportMenu()
			}
		},

		onExportMenuToggle() {
			this.showExportMenu = !this.showExportMenu

			if (this.showExportMenu) {
				this.addExportMenuListeners()
			} else {
				this.removeExportMenuListeners()
			}
		},

		onExportOutsideClick(event) {
			if (!this.$refs.exportDropdown?.contains(event.target)) {
				this.closeExportMenu()
			}
		},

		async onPrint() {
			this.closeExportMenu()

			try {
				await this.prepareForPrint()
			} catch {
				// Print the current calendar view even if refresh fails.
			}

			this.printCalendar()
		},

		async prepareForPrint() {
			await this.refreshEventsForPrint()
			this.syncCalendarEvents()

			await this.$nextTick()
		},

		async refreshEventsForPrint() {
			if (!this.activeCalendarId) {
				return
			}

			try {
				const { endsAt, startsAt } = this.getEventQueryDates()

				await this.fetchPublicCalendarEvents({
					calendarId: this.activeCalendarId,
					endsAt,
					startsAt,
				})
			} catch {
				// Allow print/PDF with an empty month when events are null or the API fails.
			}
		},

		removeExportMenuListeners() {
			document.removeEventListener('click', this.onExportOutsideClick)
			document.removeEventListener('keydown', this.onExportEscapeKeyDown)
		},

		async onSaveAsPdf() {
			this.closeExportMenu()

			try {
				await this.prepareForPrint()
			} catch {
				// Save as PDF via print dialog even if refresh fails.
			}

			this.printCalendar()
		},

		printCalendar() {
			this.freezeCalendarWidthForPrint()

			try {
				window.print()
			} finally {
				this.unfreezeCalendarWidthForPrint()
			}
		},

		onBeforePrint() {
			this.freezeCalendarWidthForPrint()
		},

		onAfterPrint() {
			this.unfreezeCalendarWidthForPrint()
		},

		freezeCalendarWidthForPrint() {
			if (this.isCalendarWidthFrozenForPrint) {
				return
			}

			const scrollgrids = [
				...(this.$el?.querySelectorAll('.fc-scrollgrid') || []),
			]

			if (!scrollgrids.length) {
				return
			}

			this.frozenPrintScrollgrids = scrollgrids
			this.isCalendarWidthFrozenForPrint = true
			scrollgrids.forEach(element => {
				element.style.width = `${element.getBoundingClientRect().width}px`
			})
			this.applyPrintFitScale()
		},

		unfreezeCalendarWidthForPrint() {
			this.clearPrintFitScale()
			;(this.frozenPrintScrollgrids || []).forEach(element => {
				element.style.width = ''
			})
			this.frozenPrintScrollgrids = []
			this.isCalendarWidthFrozenForPrint = false
		},

		applyPrintFitScale() {
			const grid = this.$el?.querySelector(
				'.public-share-calendar__grid'
			)

			if (!grid) {
				return
			}

			const gridWidth = grid.getBoundingClientRect().width

			if (!gridWidth) {
				return
			}

			const scale = Math.min(1, PRINT_CONTENT_WIDTH_PX / gridWidth)

			this.printFitGrid = grid
			grid.classList.add('public-share-calendar__grid--print-fit')

			if (
				typeof CSS !== 'undefined' &&
				CSS.supports &&
				CSS.supports('zoom', '1')
			) {
				grid.style.zoom = String(scale)
				return
			}

			grid.style.transformOrigin = 'top left'
			grid.style.transform = `scale(${scale})`
		},

		clearPrintFitScale() {
			const grid = this.printFitGrid

			if (!grid) {
				return
			}

			grid.classList.remove('public-share-calendar__grid--print-fit')
			grid.style.zoom = ''
			grid.style.transform = ''
			grid.style.transformOrigin = ''
			this.printFitGrid = null
		},

		async onMonthPillClick(date) {
			const calendarApi = this.calendarApi()

			if (!calendarApi) {
				return
			}

			calendarApi.gotoDate(date.toDate())
		},

		syncViewMonthFromCalendar() {
			const calendarApi = this.calendarApi()

			if (!calendarApi) {
				return
			}

			const { view } = calendarApi
			const nextViewMonth = moment(view.currentStart).startOf('month')

			if (nextViewMonth.isSame(this.viewMonth, 'month')) {
				return
			}

			this.syncMonthDateRange(nextViewMonth)
		},

		...mapActions({
			ensurePublicBootstrap: 'publicCalendar/ensureBootstrap',
			fetchPublicCalendarEvents: 'publicCalendar/fetchEvents',
			refreshPublicBootstrap: 'publicCalendar/refreshBootstrap',
		}),
	},

	name: 'ShareCalendar',

	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.loadCalendarData(to)
		})
	},

	beforeRouteUpdate(to, from, next) {
		if (to.params.shortId !== from.params.shortId) {
			this.stopCalendarPolling()
			this.ensurePublicBootstrap(to.params.shortId)
				.then(() => this.loadCalendarData(to))
				.finally(() => next())
			return
		}

		next()
	},

	watch: {
		loading() {
			this.$nextTick(() => this.syncGridScrollbarTrack())
		},

		'$route.params.id': {
			async handler() {
				if (this.activeCalendarId && !this.loading) {
					await this.getEvents()
				}

				this.$nextTick(() => this.syncViewMonthFromCalendar())
			},
		},
	},

	created() {
		this.syncMonthDateRange(moment().startOf('month'))

		this.fullCalendarConfig = new FullCalendarConfig()
			.set('datesSet', this.onDatesSet)
			.set('droppable', false)
			.set('editable', false)
			.set('eventDisplay', 'block')
			.set('expandRows', false)
			.set('fixedWeekCount', false)
			.set('headerToolbar', false)
			.set('height', 'auto')
			.set('initialView', 'dayGridMonth')
			.set('moreLinkClick', this.onMoreLinkClick)
			.set('navLinks', false)
			.set('selectable', false)
			.setDayMaxEventRows(COLLAPSED_EVENT_ROWS)

		// Adaptive swaps in its own print-specific layout. Dropping it keeps
		// the print/PDF output identical to what is rendered on screen.
		this.fullCalendarConfig.set(
			'plugins',
			this.fullCalendarConfig.plugins.filter(
				plugin => plugin !== adaptivePlugin
			)
		)

		this.calendarOptions = this.buildCalendarOptions()
	},

	beforeDestroy() {
		this.unfreezeCalendarWidthForPrint()
		document.removeEventListener(
			'visibilitychange',
			this.onVisibilityChange
		)
		window.removeEventListener('beforeprint', this.onBeforePrint)
		window.removeEventListener('afterprint', this.onAfterPrint)
		window.removeEventListener('resize', this.onCalendarWindowResize)
		this.stopCalendarPolling()
		this.closeExportMenu()
	},

	mounted() {
		document.addEventListener('visibilitychange', this.onVisibilityChange)
		window.addEventListener('beforeprint', this.onBeforePrint)
		window.addEventListener('afterprint', this.onAfterPrint)
		window.addEventListener('resize', this.onCalendarWindowResize)

		this.$nextTick(() => {
			this.syncGridScrollbarTrack()
			this.syncViewMonthFromCalendar()
			this.syncCalendarEvents()
		})
	},

	data() {
		const currentMonth = moment().startOf('month')

		return {
			calendarLayoutKey: 0,
			calendarOptions: null,
			endDate: currentMonth.clone().endOf('month'),
			expandDetail: false,
			fullCalendarConfig: null,
			isCalendarBusy: false,
			loading: true,
			pollIntervalId: null,
			showExportMenu: false,
			startDate: currentMonth.clone(),
			viewMonth: currentMonth.clone(),
		}
	},
}
</script>
